import React, { useState, useEffect, useRef } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Alert, Button, Spin, Typography } from 'antd';
import { useUser } from '../contexts/UserContext';
import type { UserRole } from '../contexts/UserContext';
import { brand } from '../theme';
import BrandLogomark from './common/BrandLogomark';

const { Text } = Typography;

// ── Ambient graph — background only, no labels, very low opacity ─────────────

interface GNode { x: number; y: number; vx: number; vy: number; }

const GRAPH_EDGES: [number, number][] = [
  [0,1],[0,4],[1,2],[1,5],[2,3],[2,6],[3,7],[4,8],[5,9],[6,10],
  [7,11],[8,12],[9,13],[10,14],[11,15],[12,16],[13,17],[0,9],[3,12],
  [5,14],[7,16],[1,10],[4,13],[6,15],[2,17],[8,11],[0,6],[3,9],
];
const NODE_COUNT = 18;

function AmbientGraph() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<GNode[]>([]);
  const rafRef   = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;

    nodesRef.current = Array.from({ length: NODE_COUNT }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
    }));

    function tick() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, W, H);

      const nodes = nodesRef.current;
      nodes.forEach((n) => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
      });

      // Edges
      GRAPH_EDGES.forEach(([a, b]) => {
        const na = nodes[a]; const nb = nodes[b];
        if (!na || !nb) return;
        const dist = Math.hypot(nb.x - na.x, nb.y - na.y);
        const alpha = Math.max(0, 0.10 - dist / 3600);
        ctx.beginPath();
        ctx.moveTo(na.x, na.y);
        ctx.lineTo(nb.x, nb.y);
        ctx.strokeStyle = `rgba(99,102,241,${alpha})`;
        ctx.lineWidth = 0.7;
        ctx.stroke();
      });

      // Nodes — tiny dots
      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(99,102,241,0.15)';
        ctx.fill();
      });

      rafRef.current = requestAnimationFrame(tick);
    }
    tick();
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={1400}
      height={900}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────

function IconBuilding() {
  return (
    <svg viewBox="0 0 24 24" fill="none" style={{ width: 16, height: 16 }}>
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-4h6v4M9 11h1m4 0h1M9 15h1m4 0h1"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function IconAgents() {
  return (
    <svg viewBox="0 0 24 24" fill="none" style={{ width: 16, height: 16 }}>
      <circle cx="12" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="5"  cy="18" r="2" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="19" cy="18" r="2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M12 11v4M8.5 17l3-2m5 2-3-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function IconGraph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" style={{ width: 16, height: 16 }}>
      <circle cx="5"  cy="12" r="2" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="19" cy="5"  r="2" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="19" cy="19" r="2" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M7 12h3m2 0h3M17.3 6.7l-3.3 4m0 1.5 3.3 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function IconKnowledge() {
  return (
    <svg viewBox="0 0 24 24" fill="none" style={{ width: 16, height: 16 }}>
      <path d="M12 2a7 7 0 0 1 7 7c0 3-1.8 5.6-4.5 6.7V18h-5v-2.3C6.8 14.6 5 12 5 9a7 7 0 0 1 7-7zM9.5 21h5M10 18h4"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const CAPABILITIES = [
  { icon: <IconBuilding />, text: 'Reformas residenciales y comerciales' },
  { icon: <IconAgents />,   text: 'Biblioteca de agentes especializados' },
  { icon: <IconGraph />,    text: 'Knowledge graph de cada proyecto' },
  { icon: <IconKnowledge />,text: 'Aprendizaje continuo por obra' },
];

// ── Login Page ───────────────────────────────────────────────────────────────

const DEV_USERS: { email: string; role: UserRole; label: string }[] = [
  { email: 'dev-admin@cubicup.com',  role: 'admin',  label: 'Admin'  },
  { email: 'dev-editor@cubicup.com', role: 'editor', label: 'Editor' },
  { email: 'dev-viewer@cubicup.com', role: 'viewer', label: 'Viewer' },
];

// Show the dev-login panel anywhere that is NOT real production. Local dev,
// staging Cloud Run, and PR previews qualify. The user-facing prod hostname
// (simbuild.cubicup.com) hides it.
function shouldShowDevLogin(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return true;
  if (host.includes('-staging-')) return true;
  if (host === 'simbuild.cubicup.com') return false;
  // Bare Cloud Run prod URLs of agent-builder-498112 also keep it visible
  // until DNS cuts over, so we can test there.
  return process.env.NODE_ENV !== 'production';
}
const IS_DEV = shouldShowDevLogin();

const LoginPage: React.FC = () => {
  const { login, loginAsDev } = useUser();
  const [error,   setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSuccess = async (cr: { credential?: string }) => {
    if (!cr.credential) return;
    setLoading(true); setError(null);
    const result = await login(cr.credential);
    if (!result.ok) setError(result.error ?? 'Error al iniciar sesión.');
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f7f7f8',
      fontFamily: 'Inter, system-ui, sans-serif',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Ambient graph — full background, barely visible */}
      <AmbientGraph />

      {/* Card */}
      <div style={{
        position: 'relative',
        width: 460,
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 16,
        padding: '44px 40px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }}>

        {/* Brand */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <BrandLogomark
              size={40}
              radius={10}
              fallbackIcon={<IconBuilding />}
              boxShadow="0 0 20px rgba(59,130,246,0.25)"
            />
            <div>
              <div style={{ color: '#111111', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                {brand.name}
              </div>
              <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>
                {brand.tagline}
              </div>
            </div>
          </div>

          <div style={{
            borderTop: '1px solid #f0f0f0',
            paddingTop: 24,
          }}>
            <div style={{ color: '#111111', fontSize: 22, fontWeight: 700, lineHeight: 1.3, marginBottom: 8 }}>
              IA que entiende<br />
              <span style={{ color: '#6366f1' }}>cada reforma</span>
            </div>
            <Text style={{ color: '#6b7280', fontSize: 13, lineHeight: 1.6, display: 'block' }}>
              Accede a la biblioteca de agentes especializados
              y al knowledge graph de proyectos de reforma.
            </Text>
          </div>
        </div>

        {/* Capabilities — compact list */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '8px 12px',
          marginBottom: 28,
        }}>
          {CAPABILITIES.map((c) => (
            <div key={c.text} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: '#6b7280',
              fontSize: 12,
            }}>
              <span style={{ color: '#9ca3af', flexShrink: 0 }}>{c.icon}</span>
              {c.text}
            </div>
          ))}
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', marginBottom: 24 }} />

        {/* Error */}
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}

        {/* Google sign-in */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Spin />
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GoogleLogin
              onSuccess={handleSuccess}
              onError={() => setError('Error al conectar con Google.')}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
              logo_alignment="left"
              hosted_domain="cubicup.com"
              width="380"
            />
          </div>
        )}

        <Text style={{
          display: 'block',
          textAlign: 'center',
          marginTop: 16,
          color: '#9ca3af',
          fontSize: 12,
        }}>
          Solo accesible con cuenta <strong style={{ color: '#6b7280' }}>@cubicup.com</strong>
        </Text>

        {IS_DEV && (
          <div style={{
            marginTop: 24,
            paddingTop: 20,
            borderTop: '1px dashed #e5e7eb',
          }}>
            <Text style={{
              display: 'block', textAlign: 'center', marginBottom: 12,
              color: '#9ca3af', fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase',
            }}>
              Dev login (no auth)
            </Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {DEV_USERS.map((u) => (
                <Button
                  key={u.role}
                  type={u.role === 'admin' ? 'primary' : 'default'}
                  onClick={() => loginAsDev(u.email, u.role)}
                  block
                >
                  {u.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
